import { MotionPlayIcon } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useWorkflowDefinitions } from '@/hooks'
import { cn } from '@/lib/utils'
import { Check, Loader2, Search } from 'lucide-react'
import { useState } from 'react'
import { matchesWorkflowSearch } from './shared/workflowSearch'

export interface TriggerableDefinition {
  id: string
  name: string
  displayName: string
}

interface TriggerWorkflowFromEntityDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (definition: TriggerableDefinition) => void
  linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'
  linkedEntityId?: string
}

export function TriggerWorkflowFromEntityDialog({
  projectId,
  open,
  onOpenChange,
  onSelect,
  linkedEntityType,
  linkedEntityId,
}: TriggerWorkflowFromEntityDialogProps) {
  const [selectedDefinition, setSelectedDefinition] = useState<TriggerableDefinition | null>(null)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useWorkflowDefinitions(
    projectId,
    { maxResultCount: 100, skipCount: 0 },
    { status: 'active', search: '', type: 'all', category: 'all' },
    { linkedEntityType, linkedEntityId },
  )
  const workflows = data?.items ?? []

  const filtered = workflows.filter((w) => matchesWorkflowSearch(w, search))

  const handleClose = () => {
    setSelectedDefinition(null)
    setSearch('')
    onOpenChange(false)
  }

  const handleNext = () => {
    if (!selectedDefinition) return
    const def = selectedDefinition
    setSelectedDefinition(null)
    setSearch('')
    onOpenChange(false)
    onSelect(def)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Select Workflow</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a workflow to trigger.
          </p>

          <div className="flex max-h-[440px] flex-col">
            {isLoading ? (
              <div
                className="flex items-center justify-center py-16"
                role="status"
                aria-live="polite"
                aria-label="Loading workflows"
              >
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
              </div>
            ) : workflows.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="w-full rounded-lg border border-dashed p-6 text-center"
                  data-testid="no-workflows-empty-state"
                >
                  <p className="text-sm text-muted-foreground">No workflows available.</p>
                </div>
              </div>
            ) : (
              <div className="flex h-[440px] min-h-0 flex-col gap-4">
                <div className="relative shrink-0">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    placeholder="Search by name or description"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setSelectedDefinition(null)
                    }}
                    className="pl-9"
                    aria-label="Search workflows"
                    data-testid="workflow-search-input"
                  />
                </div>

                {filtered.length === 0 ? (
                  <div
                    className="flex flex-1 items-center justify-center rounded-lg border border-dashed p-6 text-center"
                    data-testid="no-workflow-results-empty-state"
                  >
                    <p className="text-sm text-muted-foreground">No workflows match your search.</p>
                  </div>
                ) : (
                  <TooltipProvider>
                    <div
                      className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1"
                      role="listbox"
                      aria-label="Available workflows"
                    >
                      {filtered.map((workflow) => {
                        const isSelected = selectedDefinition?.id === workflow.id
                        return (
                          <button
                            key={workflow.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => setSelectedDefinition({ id: workflow.id, name: workflow.name, displayName: workflow.displayName })}
                            className={cn(
                              'w-full rounded-lg border p-3 text-left transition-colors flex items-center justify-between gap-2',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50 hover:bg-muted/50',
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="truncate text-sm font-medium">{workflow.displayName}</p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{workflow.displayName}</TooltipContent>
                                </Tooltip>
                                <Badge
                                  variant="outline"
                                  className="shrink-0 text-xs"
                                  style={{ borderColor: 'rgba(118,124,150,1)' }}
                                >
                                  {workflow.isSystem ? 'System' : 'Custom'}
                                </Badge>
                              </div>
                              {workflow.description && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                      {workflow.description}
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{workflow.description}</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={!selectedDefinition}
              data-testid="next-to-inputs-button"
            >
              <MotionPlayIcon className="h-4 w-4" />
              Continue Trigger
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
